import { useEffect, useState } from 'react';
import { registerSW } from 'virtual:pwa-register';
import { motion, AnimatePresence } from 'motion/react';
import { RefreshCw, X } from 'lucide-react';

/**
 * Service-worker registration + "Update available" toast.
 *
 * Rendered once at app shell mount. When a new SW finishes installing in the
 * background, the toast pops up; clicking "Reload" calls `updateSW(true)`
 * which activates the waiting worker and reloads the page.
 *
 * Dev note: `vite-plugin-pwa` exposes `virtual:pwa-register`; the module is
 * generated at build time, so the import is virtual-but-real in production.
 */
export default function UpdateAvailableToast() {
  const [showUpdate, setShowUpdate] = useState(false);
  const [updateSW, setUpdateSW] = useState<((reload?: boolean) => Promise<void>) | null>(null);

  useEffect(() => {
    const update = registerSW({
      onNeedRefresh() {
        setShowUpdate(true);
      },
      onRegistered(_registration) {
        // Hook for telemetry; intentionally a no-op for now.
      },
      onRegisterError(error) {
        console.warn('Service worker registration failed', error);
      },
    });
    setUpdateSW(() => update);
  }, []);

  return (
    <AnimatePresence>
      {showUpdate ? (
        <motion.div
          initial={{ y: 16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 16, opacity: 0 }}
          transition={{ duration: 0.25 }}
          role="status"
          aria-live="polite"
          className="fixed bottom-4 right-4 z-50 flex max-w-sm items-center gap-3 rounded-2xl border border-line bg-paper p-4 shadow-lg"
        >
          <RefreshCw className="h-5 w-5 shrink-0 text-ink" />
          <div className="flex-1">
            <p className="text-sm font-medium text-ink">Update available</p>
            <p className="text-xs text-ink/70">A new version of WhatImean is ready.</p>
          </div>
          <button
            type="button"
            onClick={() => updateSW?.(true)}
            className="rounded-full border border-bg-inverse bg-ink px-4 py-1.5 text-xs font-medium text-paper"
          >
            Reload
          </button>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setShowUpdate(false)}
            className="text-muted hover:text-ink"
          >
            <X className="h-4 w-4" />
          </button>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
